import { kpiFieldName } from '../kpi-names';
import { keyedName } from '../vocabulary/vocabulary';
import {
  FieldPath,
  FilterExpr,
  FilterLiteral,
  FilterOp,
  PathSegment,
  Quantifier,
  RelativeDateKeyword,
  RelativeDateSpec
} from '../wire';
import { DslError, Token, TokenKind, isIsoDuration, tokenize } from './lexer';
import { RELATIVE_DATE_DSL, printStr } from './printer';

export { isIsoDuration };

export type DslResult<T> = { ok: true; value: T } | { ok: false; error: DslError };

export const RELATIVE_DATE_BY_DSL: ReadonlyMap<string, RelativeDateKeyword> = new Map(
  (Object.entries(RELATIVE_DATE_DSL) as [RelativeDateKeyword, string][]).map(
    ([wire, dsl]) => [dsl, wire]
  )
);
const COMPARISONS: Partial<
  Record<
    TokenKind,
    | 'equals'
    | 'not_equals'
    | 'greater_than'
    | 'greater_than_or_equal'
    | 'less_than'
    | 'less_than_or_equal'
  >
> = {
  '==': 'equals',
  '!=': 'not_equals',
  '>': 'greater_than',
  '>=': 'greater_than_or_equal',
  '<': 'less_than',
  '<=': 'less_than_or_equal'
};
const LABEL_COMPARISONS = {
  equals: 'label_equals',
  not_equals: 'label_not_equals',
  greater_than: 'label_greater_than',
  greater_than_or_equal: 'label_greater_than_or_equal',
  less_than: 'label_less_than',
  less_than_or_equal: 'label_less_than_or_equal'
} as const;

export const OPERATOR_START_WORDS: ReadonlySet<string> = new Set([
  'between',
  'in',
  'not',
  'contains',
  'is',
  'starts',
  'ends',
  'matches',
  'has',
  'label'
]);

export function isOperatorStart(token: Token | undefined): boolean {
  if (!token) return false;
  if (token.kind in COMPARISONS) return true;
  return token.kind === 'word' && OPERATOR_START_WORDS.has(token.value);
}

export function literalFromQuantity(token: Token): FilterLiteral {
  const value = token.number!;
  switch (token.unit) {
    case 'g':
      return { mass: { value, unit: 'Gram' } };
    case 'kg':
      return { mass: { value, unit: 'Kilogram' } };
    case 't':
      return { mass: { value, unit: 'Ton' } };
    case 'mm':
      return { length: { value, unit: 'Millimeter' } };
    case 'cm':
      return { length: { value, unit: 'Centimeter' } };
    case 'm':
      return { length: { value, unit: 'Meter' } };
    default:
      return { length: { value, unit: 'Kilometer' } };
  }
}

function isRealDate(text: string): boolean {
  const date = new Date(`${text}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === text;
}

export class DslParser {
  protected pos = 0;

  constructor(
    protected readonly tokens: Token[],
    protected readonly input: string
  ) {}

  peek(offset = 0): Token | undefined {
    return this.tokens[this.pos + offset];
  }

  atEnd(): boolean {
    return this.pos >= this.tokens.length;
  }

  isWord(word: string, offset = 0): boolean {
    const token = this.peek(offset);
    return token?.kind === 'word' && token.value === word;
  }

  eatWord(word: string): boolean {
    if (!this.isWord(word)) return false;
    this.pos++;
    return true;
  }

  expect(kind: TokenKind, expected: string): Token {
    const token = this.peek();
    if (token?.kind !== kind) this.fail(expected);
    this.pos++;
    return token;
  }

  fail(expected: string): never {
    const token = this.peek();
    if (!token) {
      throw new DslError(this.input.length, this.input.length, expected, 'end of input');
    }
    const found =
      token.kind === 'str'
        ? printStr(token.value)
        : this.input.slice(token.start, token.end);
    throw new DslError(token.start, token.end, expected, found);
  }

  parseFilter(): FilterExpr {
    const first = this.parseAnd();
    const items = [first];
    while (this.eatWord('or')) items.push(this.parseAnd());
    return items.length === 1 ? first : { or: items };
  }

  private parseAnd(): FilterExpr {
    const first = this.parseUnary();
    const items = [first];
    while (this.eatWord('and')) items.push(this.parseUnary());
    return items.length === 1 ? first : { and: items };
  }

  parseUnary(): FilterExpr {
    if (this.eatWord('not')) return { not: this.parseUnary() };
    return this.parseAtom();
  }

  looksLikeAssociation(): boolean {
    const edge = this.peek();
    const quantifier = this.peek(2);
    return (
      (edge?.kind === 'word' || edge?.kind === 'name') &&
      this.peek(1)?.kind === '[' &&
      quantifier?.kind === 'word' &&
      (quantifier.value === 'any' || quantifier.value === 'all') &&
      this.peek(3)?.kind === ']' &&
      this.peek(4)?.kind === '('
    );
  }

  private parseAtom(): FilterExpr {
    if (this.peek()?.kind === '(') {
      this.pos++;
      const inner = this.parseFilter();
      this.expect(')', '`)` to close the group');
      return inner;
    }
    if ((this.isWord('all') || this.isWord('any')) && this.peek(1)?.kind === '(') {
      const connector = this.peek()!.value;
      this.pos += 2;
      const items: FilterExpr[] = [];
      if (this.peek()?.kind !== ')') {
        items.push(this.parseFilter());
        while (this.peek()?.kind === ',') {
          this.pos++;
          items.push(this.parseFilter());
        }
      }
      this.expect(')', '`)` to close the argument list');
      return connector === 'all' ? { and: items } : { or: items };
    }
    if (this.looksLikeAssociation()) {
      const edge = this.peek()!.value;
      this.pos += 2;
      const quantifier = this.parseQuantifier();
      this.expect(']', '`]` after the quantifier');
      this.expect('(', '`(` to open the association');
      const filter = this.parseFilter();
      this.expect(')', '`)` to close the association');
      return { association: { edge, quantifier, filter } };
    }
    const path = this.parsePath();
    return { predicate: { path, op: this.parseOp() } };
  }

  parsePath(): FieldPath {
    const segments: PathSegment[] = [];
    for (;;) {
      const name = this.parseKpiName() ?? this.parseSegmentName();
      if (this.peek()?.kind === '[') {
        this.pos++;
        const quantifier = this.parseQuantifier();
        this.expect(']', '`]` after the quantifier');
        segments.push({ collection: { name, quantifier } });
      } else {
        segments.push({ field: { name } });
      }
      if (this.peek()?.kind !== '.') return { segments };
      this.pos++;
    }
  }

  private parseSegmentName(): string {
    const token = this.peek();
    if (!token || (token.kind !== 'word' && token.kind !== 'name'))
      this.fail('a field name');
    this.pos++;
    return token.value;
  }

  /**
   * `kpi.<name>` and `kpi.<name>[<key>]` are one segment, the KPI field `#assoc:kpi.<name>`: no
   * association is named `kpi`, and a field that is would be written backticked. `[any]` and `[all]`
   * after the name stay quantifiers. The number of tokens such a name spans `offset` tokens ahead, 0
   * when there is none.
   */
  protected kpiNameLength(offset = 0): number {
    const name = this.peek(offset + 2);
    if (
      !this.isWord('kpi', offset) ||
      this.peek(offset + 1)?.kind !== '.' ||
      (name?.kind !== 'word' && name?.kind !== 'name')
    )
      return 0;
    const key = this.peek(offset + 4);
    const keyed =
      this.peek(offset + 3)?.kind === '[' &&
      key?.kind === 'word' &&
      key.value !== 'any' &&
      key.value !== 'all' &&
      this.peek(offset + 5)?.kind === ']';
    return keyed ? 6 : 3;
  }

  protected parseKpiName(): string | null {
    const length = this.kpiNameLength();
    if (length === 0) return null;
    const name = this.peek(2)!.value;
    const field = kpiFieldName(length === 6 ? keyedName(name, this.peek(4)!.value) : name);
    this.pos += length;
    return field;
  }

  private parseQuantifier(): Quantifier {
    if (this.eatWord('any')) return 'any';
    if (this.eatWord('all')) return 'all';
    this.fail('`any` or `all`');
  }

  private parseOp(): FilterOp {
    const comparison = COMPARISONS[this.peek()?.kind as TokenKind];
    if (comparison) {
      this.pos++;
      return { [comparison]: this.parseLiteral() } as FilterOp;
    }
    if (this.eatWord('between')) return { between: this.parseRange() };
    if (this.eatWord('in')) return { is_any_of: this.parseLiteralList() };
    if (this.eatWord('not')) {
      if (this.eatWord('in')) return { is_none_of: this.parseLiteralList() };
      if (this.eatWord('contains')) {
        if (this.eatWord('all')) return { not_contains_all: this.parseLiteralList() };
        return { not_contains: this.parseStrLit() };
      }
      this.fail('`in` or `contains` after `not`');
    }
    if (this.eatWord('contains')) {
      if (this.eatWord('all')) return { contains_all: this.parseLiteralList() };
      return { contains: this.parseStrLit() };
    }
    if (this.eatWord('is')) {
      if (this.eatWord('empty')) return 'is_empty';
      if (this.eatWord('not')) {
        if (!this.eatWord('empty')) this.fail('`empty` after `is not`');
        return 'is_not_empty';
      }
      return { relative_date: this.parseRelativeDate() };
    }
    if (this.eatWord('starts')) {
      if (!this.eatWord('with')) this.fail('`with` after `starts`');
      return { starts_with: this.parseStrLit() };
    }
    if (this.eatWord('ends')) {
      if (!this.eatWord('with')) this.fail('`with` after `ends`');
      return { ends_with: this.parseStrLit() };
    }
    if (this.eatWord('matches')) return { matches_regex: this.parseStrLit() };
    if (this.eatWord('has')) return this.parseHasOp();
    if (this.eatWord('label')) return this.parseLabelOp();
    this.fail('an operator');
  }

  private parseRange(): { lo: FilterLiteral; hi: FilterLiteral } {
    const lo = this.parseLiteral();
    this.expect('..', '`..` between the `between` bounds');
    return { lo, hi: this.parseLiteral() };
  }

  private parseRelativeDate(): RelativeDateSpec {
    if (this.eatWord('within')) {
      const token = this.peek();
      if (token?.kind !== 'int') this.fail('an integer day count after `within`');
      this.pos++;
      return { within: token.number! };
    }
    const token = this.peek();
    const keyword =
      token?.kind === 'word' ? RELATIVE_DATE_BY_DSL.get(token.value) : undefined;
    if (!keyword) this.fail('a relative date keyword');
    this.pos++;
    return keyword;
  }

  private parseHasOp(): FilterOp {
    if (this.eatWord('tag')) return { has_tag: this.parseStrLit() };
    if (this.eatWord('label')) return { has_label: this.parseStrLit() };
    if (this.eatWord('no')) {
      if (this.eatWord('tag')) return { not_has_tag: this.parseStrLit() };
      if (this.eatWord('label')) return { not_has_label: this.parseStrLit() };
      this.fail('`tag` or `label` after `has no`');
    }
    if (this.eatWord('any')) {
      if (!this.eatWord('tag')) this.fail('`tag` after `has any`');
      return { has_any_tag: this.parseStrList() };
    }
    if (this.eatWord('all')) {
      if (!this.eatWord('tags')) this.fail('`tags` after `has all`');
      return { has_all_tags: this.parseStrList() };
    }
    if (this.eatWord('none')) {
      if (!this.eatWord('of')) this.fail('`of` after `has none`');
      if (!this.eatWord('tags')) this.fail('`tags` after `has none of`');
      return { has_none_of_tags: this.parseStrList() };
    }
    this.fail('`tag`, `label`, `no`, `any`, `all`, or `none` after `has`');
  }

  private parseLabelOp(): FilterOp {
    const label = this.parseStrLit();
    const comparison = COMPARISONS[this.peek()?.kind as TokenKind];
    if (comparison) {
      this.pos++;
      return {
        [LABEL_COMPARISONS[comparison]]: { label, value: this.parseLiteral() }
      } as FilterOp;
    }
    if (this.eatWord('between')) return { label_between: { label, ...this.parseRange() } };
    if (this.eatWord('in')) return { label_in: { label, values: this.parseLiteralList() } };
    if (this.eatWord('not')) {
      if (!this.eatWord('in')) this.fail('`in` after `label <key> not`');
      return { label_not_in: { label, values: this.parseLiteralList() } };
    }
    if (this.eatWord('contains'))
      return { label_contains: { label, value: this.parseStrLit() } };
    if (this.eatWord('starts')) {
      if (!this.eatWord('with')) this.fail('`with` after `starts`');
      return { label_starts_with: { label, value: this.parseStrLit() } };
    }
    if (this.eatWord('ends')) {
      if (!this.eatWord('with')) this.fail('`with` after `ends`');
      return { label_ends_with: { label, value: this.parseStrLit() } };
    }
    if (this.eatWord('matches')) {
      return { label_matches_regex: { label, value: this.parseStrLit() } };
    }
    if (this.eatWord('references')) {
      if (this.eatWord('any')) {
        return { label_references_any_of: { label, values: this.parseLiteralList() } };
      }
      if (this.eatWord('none')) {
        return { label_references_none_of: { label, values: this.parseLiteralList() } };
      }
      return { label_references: { label, value: this.parseLiteral() } };
    }
    this.fail('a label operator');
  }

  parseLiteral(): FilterLiteral {
    const token = this.peek();
    switch (token?.kind) {
      case 'int':
        this.pos++;
        return { integer: token.number! };
      case 'num':
        this.pos++;
        return { number: token.number! };
      case 'str':
        this.pos++;
        return { string: token.value };
      case 'quantity':
        this.pos++;
        return literalFromQuantity(token);
      case 'duration':
        if (!isIsoDuration(token.value)) this.fail('a valid ISO 8601 duration');
        this.pos++;
        return { duration: token.value };
      case 'date':
        if (!isRealDate(token.value)) this.fail('a valid date');
        this.pos++;
        return { date: token.value };
      case 'date_time':
        if (!isRealDate(token.value.slice(0, 10)) || Number.isNaN(Date.parse(token.value)))
          this.fail('a valid RFC 3339 timestamp');
        this.pos++;
        return { date_time: token.value };
      case '[':
        return { list: this.parseLiteralList() };
      case 'word':
        return this.parseWordLiteral(token);
      default:
        this.fail('a literal');
    }
  }

  private parseWordLiteral(token: Token): FilterLiteral {
    if (token.value === 'true' || token.value === 'false') {
      this.pos++;
      return { bool: token.value === 'true' };
    }
    if (token.value === 'null') {
      this.pos++;
      return 'null';
    }
    if (token.value === 'uid' && this.peek(1)?.kind === '(') {
      this.pos += 2;
      const uid = this.parseStrLit('a quoted uid');
      this.expect(')', '`)`');
      return { uid };
    }
    if (token.value === 'uids' && this.peek(1)?.kind === '[') {
      this.pos++;
      return { uid_list: this.parseStrList('a quoted uid') };
    }
    if (token.value === 'self') {
      this.pos++;
      if (this.eatWord('label')) return { self: { label: this.parseStrLit() } };
      return { self: { field: this.parsePath() } };
    }
    this.fail('a literal');
  }

  private parseLiteralList(): FilterLiteral[] {
    this.expect('[', '`[`');
    const items: FilterLiteral[] = [];
    if (this.peek()?.kind !== ']') {
      items.push(this.parseLiteral());
      while (this.peek()?.kind === ',') {
        this.pos++;
        items.push(this.parseLiteral());
      }
    }
    this.expect(']', '`]`');
    return items;
  }

  private parseStrList(expected = 'a quoted string'): string[] {
    this.expect('[', '`[`');
    const items: string[] = [];
    if (this.peek()?.kind !== ']') {
      items.push(this.parseStrLit(expected));
      while (this.peek()?.kind === ',') {
        this.pos++;
        items.push(this.parseStrLit(expected));
      }
    }
    this.expect(']', '`]`');
    return items;
  }

  private parseStrLit(expected = 'a quoted string'): string {
    const token = this.peek();
    if (token?.kind !== 'str') this.fail(expected);
    this.pos++;
    return token.value;
  }
}

export function parseFilter(text: string): DslResult<FilterExpr> {
  try {
    const parser = new DslParser(tokenize(text), text);
    const value = parser.parseFilter();
    if (!parser.atEnd()) {
      const leftover = parser.peek()!;
      parser.fail(
        leftover.kind === ')' || leftover.kind === ']'
          ? 'end of input'
          : 'an operator or end of input'
      );
    }
    return { ok: true, value };
  } catch (error) {
    if (error instanceof DslError) return { ok: false, error };
    throw error;
  }
}
